/**
 * Enum que define os níveis de permissão/função de uma conta de usuário no sistema.
 *
 * @enum {string}
 * @readonly
 */
export enum AccountRole {
  /** Proprietário do sistema - acesso total */
  Owner = 'OWNER',

  /** Administrador - permissões administrativas */
  Admin = 'ADMIN',

  /** Usuário padrão - permissões básicas */
  User = 'USER',
}

/**
 * Converte um valor desconhecido para um AccountRole válido.
 *
 * Esta função realiza validação type-safe do valor recebido, garantindo que:
 * - O valor seja uma string
 * - O valor corresponda a um dos valores válidos do enum AccountRole
 * - Retorne um fallback seguro (User) caso o valor seja inválido
 *
 * @param {unknown} value - Valor a ser convertido (geralmente vindo do backend)
 * @returns {AccountRole} Role válido ou AccountRole.User como padrão
 *
 * @example
 * ```typescript
 * // Valor válido
 * const role = parseAccountRole('ADMIN'); // AccountRole.Admin
 *
 * // Valor inválido
 * const role = parseAccountRole('INVALID'); // AccountRole.User
 *
 * // Tipos inválidos
 * const role = parseAccountRole(null); // AccountRole.User
 * const role = parseAccountRole(123); // AccountRole.User
 * const role = parseAccountRole(undefined); // AccountRole.User
 * ```
 */
export function parseAccountRole(value: unknown): AccountRole {
  // Validação de tipo: garante que o valor é uma string
  if (typeof value !== 'string') {
    return AccountRole.User
  }

  // Validação de valor: verifica se a string corresponde a um valor válido do enum
  return Object.values(AccountRole).includes(value as AccountRole)
    ? (value as AccountRole)
    : AccountRole.User
}

/**
 * Verifica se um valor string é um AccountRole válido.
 *
 * @param {string} value - Valor a ser validado
 * @returns {boolean} true se o valor é um AccountRole válido, false caso contrário
 *
 * @example
 * ```typescript
 * isValidAccountRole('ADMIN'); // true
 * isValidAccountRole('INVALID'); // false
 * ```
 */
export function isValidAccountRole(value: string): value is AccountRole {
  return Object.values(AccountRole).includes(value as AccountRole)
}

/**
 * Retorna uma lista de todos os valores válidos do AccountRole.
 *
 * @returns {AccountRole[]} Array com todos os valores do enum
 *
 * @example
 * ```typescript
 * const roles = getAllAccountRoles();
 * // ['OWNER', 'ADMIN', 'USER']
 * ```
 */
export function getAllAccountRoles(): AccountRole[] {
  return Object.values(AccountRole)
}

/**
 * Retorna um label legível em português para cada role.
 *
 * @param {AccountRole} role - Role para obter o label
 * @returns {string} Label traduzido do role
 *
 * @example
 * ```typescript
 * getAccountRoleLabel(AccountRole.Admin); // 'Administrador'
 * ```
 */
export function getAccountRoleLabel(role: AccountRole): string {
  const labels: Record<AccountRole, string> = {
    [AccountRole.Owner]: 'Proprietário',
    [AccountRole.Admin]: 'Administrador',
    [AccountRole.User]: 'Usuário',
  }

  return labels[role]
}

// VERSÃO ANTIGA
// export enum AccountRole {
//   Owner = 'OWNER',
//   Admin = 'ADMIN',
//   User = 'USER',
// }
